import React, { useState, useEffect, useRef } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppSelect from 'src/components/common/AppSelect';
import DataTable from 'src/components/ui/data-table';
import {
  Info,
  CheckCircle,
  Clock,
  DollarSign,
  File,
  FileText,
} from 'lucide-react';
import BillingTabs from 'src/pages/Billing/BillingTabs/BillingTabs';
import SearchInput from 'src/components/common/SearchInput';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'src/components/ui/tooltip';
import { StatusBadge } from 'src/components/common/StatusBadge';
import StatsSection from 'src/components/common/StatsSection';
import {
  useBillingList,
  useBillingSummary,
} from 'src/services/api/queries/billingQueries';

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `${num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatCompact = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_00_00_000)
    return `${sign}${(abs / 1_00_00_000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (abs >= 1_00_000)
    return `${sign}${(abs / 1_00_000).toFixed(2).replace(/\.?0+$/, '')}L`;
  if (abs >= 1_000)
    return `${sign}${(abs / 1_000).toFixed(2).replace(/\.?0+$/, '')}K`;
  return `${sign}${abs.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const getColumns = () => [
  {
    key: 'po_number',
    header: 'Work Order',
    width: '42%',
    render: (value, row) => (
      <div>
        <div className="font-mono text-[12.5px] font-semibold text-primary tracking-[0.02em] leading-tight">
          {value}
        </div>
        {row.organization_name && (
          <div className="text-[12px] text-muted-foreground font-[450] mt-0.5 leading-tight">
            {row.organization_name}
          </div>
        )}
        {row.description && (
          <div className="text-[12px] text-muted-foreground font-[400] mt-0.5 leading-snug line-clamp-2">
            {row.description}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'total_amount',
    header: (
      <div className="flex items-center gap-1 justify-end w-full">
        Amount
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="p-0 m-0 bg-transparent border-0 cursor-pointer"
                aria-label="GST exclusive"
              >
                <Info className="w-3.5 h-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>GST exclusive</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
    width: '16%',
    align: 'right',
    render: (value) => (
      <span
        className="text-[13px] font-semibold text-foreground-strong whitespace-nowrap"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {formatAmount(value)}
      </span>
    ),
  },
  {
    key: 'candidate_count',
    header: 'Candidates',
    width: '12%',
    align: 'center',
    render: (value) => (
      <span
        className="text-[13px] font-[550] text-foreground-strong"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value ?? '—'}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '16%',
    render: (value) => <StatusBadge value={value} />,
  },
];

const DEFAULT_PAGE_SIZE = 10;

function ListBilling() {
  const navigate = useNavigate();
  const { data: summaryData } = useBillingSummary('work_order');
  const wo = summaryData?.data?.work_order ?? {};
  const woTotalValue = wo.total_amount ?? wo.total_value;
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Sort — sortBy is fixed to 'created_at' for now (no column-picker yet,
  // just the one field), sortOrder is the only thing the dropdown actually
  // controls.
  const [sortOrder, setSortOrder] = useState('desc');
  const prevFiltersRef = useRef({
    search: '',
    status: 'all',
    sortOrder: 'desc',
  });

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );

  // Server-side list: page / size / search / status are all part of the
  // queryKey, so changing any of them refetches automatically.
  const { data: billingResponse, isFetching } = useBillingList({
    page: currentPage,
    pageSize: currentPageSize,
    search: debouncedSearch || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    sortBy: 'created_at',
    sortOrder,
  });

  const purchaseOrders =
    billingResponse?.data?.items || billingResponse?.items || [];
  const metrics = billingResponse?.data?.metrics ?? null;
  const pagination =
    billingResponse?.data?.pagination || billingResponse?.pagination || {};
  const totalItems = pagination.total ?? 0;
  const totalPages =
    pagination.pages ||
    Math.ceil((totalItems || 0) / (currentPageSize || DEFAULT_PAGE_SIZE)) ||
    1;

  // Debounce the search box before hitting the API
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const columns = getColumns();

  const updateUrlParams = (params) => {
    const updatedParams = new URLSearchParams(searchParams);
    if (params.page) updatedParams.set('page', params.page.toString());
    if (params.pageSize)
      updatedParams.set('pageSize', params.pageSize.toString());
    setSearchParams(updatedParams);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: parseInt(newPage, 10) });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateUrlParams({ pageSize: parseInt(newPageSize, 10), page: 1 });
  };

  // Keep pageSize in the URL (cosmetic, one-time on mount)
  useEffect(() => {
    if (!searchParams.has('pageSize')) {
      updateUrlParams({ pageSize: DEFAULT_PAGE_SIZE });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side fetch whenever page, size, search, or status changes.
  // When a filter changes we snap back to page 1 first, then fetch.
  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.search !== debouncedSearch ||
      prevFiltersRef.current.status !== statusFilter ||
      prevFiltersRef.current.sortOrder !== sortOrder;
    prevFiltersRef.current = {
      search: debouncedSearch,
      status: statusFilter,
      sortOrder,
    };

    if (filtersChanged && currentPage !== 1) {
      updateUrlParams({ page: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageSize, debouncedSearch, statusFilter, sortOrder]);

  const hasFilters = statusFilter !== 'all' || searchQuery.trim() !== '';

  const emptyState = (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
        <div
          className="flex items-center justify-center mb-5 rounded-full bg-border"
          style={{ width: 72, height: 72 }}
        >
          <FileText
            className="text-muted-foreground"
            style={{ width: 32, height: 32, strokeWidth: 1.5 }}
          />
        </div>
        <p className="text-[15px] font-semibold text-foreground-strong mb-1.5 tracking-[-0.01em]">
          No work orders found
        </p>
        <p className="text-[13px] text-muted-foreground leading-relaxed max-w-xs">
          {hasFilters
            ? 'Try adjusting your filters or search query.'
            : 'Work orders will appear here once they are created.'}
        </p>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Work Orders" />
      <PageContainer>
        {/* Stats section */}
        <StatsSection
          title="Summary"
          className="mb-5"
          cards={[
            {
              key: 'total',
              label: 'Total Work Orders',
              value: wo.total_count ?? 0,
              iconBg: '#E9F1FA',
              iconColor: '#2068B2',
              icon: (
                <File style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'pending',
              label: 'Pending Approval',
              value: wo.status_breakdown?.PENDING_APPROVAL ?? 0,
              iconBg: '#FFFBEB',
              iconColor: '#B45309',
              icon: (
                <Clock style={{ width: 20, height: 20, strokeWidth: 1.8 }} />
              ),
            },
            {
              key: 'approved',
              label: 'Approved',
              value: wo.status_breakdown?.APPROVED ?? 0,
              iconBg: '#ECFDF5',
              iconColor: '#0D9668',
              icon: (
                <CheckCircle
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
            {
              key: 'value',
              label: 'Total Value',
              value: woTotalValue != null ? formatCompact(woTotalValue) : 0,
              iconBg: '#F3F0FF',
              iconColor: '#7C3AED',
              icon: (
                <DollarSign
                  style={{ width: 20, height: 20, strokeWidth: 1.8 }}
                />
              ),
            },
          ]}
        />

        {/* Tabs */}
        <BillingTabs metrics={metrics} />

        {/* Filter bar */}
        <div className="flex items-center gap-2.5 mb-4">
          <AppSelect
            value={statusFilter}
            onValueChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All Statuses' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'PENDING_APPROVAL', label: 'Pending Approval' },
              { value: 'APPROVED', label: 'Approved' },
            ]}
            className="h-auto py-2 text-[12.5px] font-[500] w-[180px]"
          />
          <AppSelect
            value={sortOrder}
            onValueChange={setSortOrder}
            options={[
              { value: 'desc', label: 'Sort: Newest first' },
              { value: 'asc', label: 'Sort: Oldest first' },
            ]}
            className="h-auto py-2 text-[12.5px] font-[500] w-[170px]"
          />
          <div className="flex-1" />
          <SearchInput
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search work orders..."
            className="w-52"
          />
        </div>

        <DataTable
          data={purchaseOrders}
          columns={columns}
          loading={isFetching}
          rowKey="id"
          className="border-border shadow-[0_1px_3px_rgba(0,0,0,.04)]"
          hover
          stickyHeader
          tableLayout="fixed"
          onRowClick={(row) => navigate(`/purchase-order/${row?.id}`)}
          pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyState={emptyState}
        />
      </PageContainer>
    </div>
  );
}

export default ListBilling;
